import React, { useState, useEffect } from 'react';
import { Users, Shield, Award, Plus, Lock, Unlock, LogOut, ShieldCheck } from 'lucide-react';

const AdminDashboard = ({ onNavigate, onLogout }) => {
    const [activeTab, setActiveTab] = useState('users');
    const [blockedUsers, setBlockedUsers] = useState(() => {
        try { return JSON.parse(localStorage.getItem('blockedUsers')) || []; } catch { return []; }
    });

    // Mock users list - in a real app this would come from a backend
    const [users] = useState([
        { id: 1, name: "Ramesh Kumar", email: "ramesh@example.com", role: "Farmer" },
        { id: 2, name: "Suresh Singh", email: "suresh@example.com", role: "Farmer" },
        { id: 3, name: "Anita Devi", email: "anita@example.com", role: "Expert" },
        { id: 4, name: "Rahul Verma", email: "rahul@example.com", role: "Buyer" },
    ]);

    // Scheme Form State
    const [newScheme, setNewScheme] = useState({
        title: '',
        description: '',
        category: 'central',
        benefits: '',
        eligibility: '',
        link: ''
    });

    const [schemes, setSchemes] = useState(() => {
        try {
            const stored = JSON.parse(localStorage.getItem('schemes'));
            return stored || null;
        } catch { return null; }
    });

    useEffect(() => {
        localStorage.setItem('blockedUsers', JSON.stringify(blockedUsers));
    }, [blockedUsers]);

    const toggleBlockUser = (email) => {
        if (blockedUsers.includes(email)) {
            setBlockedUsers(blockedUsers.filter(u => u !== email));
        } else {
            setBlockedUsers([...blockedUsers, email]);
        }
    };

    const [verifiedUsers, setVerifiedUsers] = useState(() => {
        try { return JSON.parse(localStorage.getItem('verifiedUsers')) || []; } catch { return []; }
    });

    useEffect(() => {
        localStorage.setItem('verifiedUsers', JSON.stringify(verifiedUsers));
    }, [verifiedUsers]);

    const toggleVerify = (email) => {
        if (verifiedUsers.includes(email)) {
            setVerifiedUsers(verifiedUsers.filter(e => e !== email));
        } else {
            setVerifiedUsers([...verifiedUsers, email]);
        }
    };

    const handleSchemeSubmit = (e) => {
        e.preventDefault();

        const schemeData = {
            id: Date.now(),
            title: newScheme.title,
            description: newScheme.description,
            benefits: newScheme.benefits.split(',').map(b => b.trim()),
            eligibility: newScheme.eligibility,
            link: newScheme.link
        };

        // We need to read the current schemes structure first if it exists, roughly mimicking Schemes.jsx structure
        const currentSchemes = schemes || { central: [], state: [], other: [] };

        // Add to specific category
        currentSchemes[newScheme.category] = [...(currentSchemes[newScheme.category] || []), schemeData];

        setSchemes(currentSchemes);
        localStorage.setItem('schemes', JSON.stringify(currentSchemes));

        // Reset form
        setNewScheme({
            title: '',
            description: '',
            category: 'central',
            benefits: '',
            eligibility: '',
            link: ''
        });

        alert('Scheme Posted Successfully!');
    };

    const [sidebarOpen, setSidebarOpen] = useState(false); // Mobile sidebar state

    return (
        <div className="min-h-screen bg-slate-50 relative">

            {/* Mobile Sidebar Toggle */}
            <div className="fixed bottom-6 right-6 lg:hidden z-50">
                <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="bg-slate-900 text-white p-4 rounded-full shadow-2xl hover:bg-slate-800 transition-all active:scale-95"
                >
                    <Users className="w-6 h-6" />
                </button>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">

                {/* Header Card */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 bg-white/80 backdrop-blur-lg p-6 rounded-3xl shadow-sm border border-white/20">
                    <div className="flex items-center space-x-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg shadow-red-500/20">
                            <Shield className="w-7 h-7 text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-black text-slate-800 tracking-tight">Super Admin</h1>
                            <p className="text-slate-500 font-medium">Control Center</p>
                        </div>
                    </div>
                    <button onClick={onLogout} className="flex items-center space-x-2 px-5 py-3 bg-red-50 text-red-600 rounded-xl font-bold hover:bg-red-100 transition-colors group">
                        <LogOut className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                        <span>End Session</span>
                    </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 items-start relative">

                    {/* Sidebar Navigation - Sticky on Desktop, Sliding on Mobile */}
                    <div className={`
                        lg:block 
                        ${sidebarOpen ? 'fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm lg:relative lg:bg-transparent lg:inset-auto lg:backdrop-blur-none' : 'hidden'}
                    `}>
                        <div className={`
                            space-y-3 bg-white/80 backdrop-blur-xl lg:bg-transparent lg:backdrop-blur-none p-6 lg:p-0 rounded-3xl lg:rounded-none h-full lg:h-auto
                            ${sidebarOpen ? 'w-3/4 absolute left-0 top-0 h-full shadow-2xl animate-slide-in-right' : ''}
                        `}>
                            {/* Mobile Header in Menu */}
                            {sidebarOpen && <div className="lg:hidden mb-6 text-2xl font-black text-slate-800">Menu</div>}

                            <button
                                onClick={() => { setActiveTab('users'); setSidebarOpen(false); }}
                                className={`w-full text-left px-6 py-5 rounded-2xl font-bold flex items-center space-x-4 transition-all duration-300 group
                                    ${activeTab === 'users'
                                        ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-500/30 scale-105'
                                        : 'bg-white text-slate-600 hover:bg-white hover:shadow-md hover:scale-[1.02]'}
                                `}
                            >
                                <div className={`p-2 rounded-lg ${activeTab === 'users' ? 'bg-white/20' : 'bg-emerald-50 group-hover:bg-emerald-100'} transition-colors`}>
                                    <Users className={`w-5 h-5 ${activeTab === 'users' ? 'text-white' : 'text-emerald-600'}`} />
                                </div>
                                <span className="text-lg">User List</span>
                            </button>
                            <button
                                onClick={() => { setActiveTab('schemes'); setSidebarOpen(false); }}
                                className={`w-full text-left px-6 py-5 rounded-2xl font-bold flex items-center space-x-4 transition-all duration-300 group
                                    ${activeTab === 'schemes'
                                        ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-500/30 scale-105'
                                        : 'bg-white text-slate-600 hover:bg-white hover:shadow-md hover:scale-[1.02]'}
                                `}
                            >
                                <div className={`p-2 rounded-lg ${activeTab === 'schemes' ? 'bg-white/20' : 'bg-emerald-50 group-hover:bg-emerald-100'} transition-colors`}>
                                    <Award className={`w-5 h-5 ${activeTab === 'schemes' ? 'text-white' : 'text-emerald-600'}`} />
                                </div>
                                <span className="text-lg">New Scheme</span>
                            </button>
                        </div>
                        {/* Overlay closer */}
                        {sidebarOpen && <div className="flex-1 lg:hidden" onClick={() => setSidebarOpen(false)}></div>}
                    </div>

                    {/* Main Content Area */}
                    <div className="lg:col-span-3">

                        {/* User Management Tab */}
                        {activeTab === 'users' && (
                            <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl border border-white/20 overflow-hidden animate-fade-in-up">
                                <div className="p-8 border-b border-slate-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                    <div>
                                        <h2 className="text-2xl font-black text-slate-800">User Database</h2>
                                        <p className="text-slate-500 font-medium mt-1">Manage platform access rights</p>
                                    </div>
                                    <div className="px-4 py-2 bg-emerald-50 text-emerald-700 rounded-xl font-bold text-sm">
                                        Total Users: {users.length}
                                    </div>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left">
                                        <thead className="bg-slate-50/50 text-slate-400 text-xs uppercase tracking-wider font-bold">
                                            <tr>
                                                <th className="px-8 py-5">User Profile</th>
                                                <th className="px-8 py-5">Role</th>
                                                <th className="px-8 py-5">Status</th>
                                                <th className="px-8 py-5 text-center">Verified?</th>
                                                <th className="px-8 py-5 text-right w-32">Controls</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {users.map(user => {
                                                const isBlocked = blockedUsers.includes(user.email);
                                                const isVerified = verifiedUsers.includes(user.email);

                                                return (
                                                    <tr key={user.id} className="hover:bg-slate-50/80 transition-colors group">
                                                        <td className="px-8 py-5">
                                                            <div className="flex items-center space-x-4">
                                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-indigo-600 font-bold border-2 border-white shadow-sm relative">
                                                                    {user.name.charAt(0)}
                                                                    {isVerified && <div className="absolute -bottom-1 -right-1 bg-blue-500 text-white rounded-full p-0.5 border-2 border-white"><svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg></div>}
                                                                </div>
                                                                <div>
                                                                    <div className="font-bold text-slate-800 group-hover:text-emerald-700 transition-colors flex items-center space-x-1">
                                                                        <span>{user.name}</span>
                                                                        {isVerified && <div className="text-blue-500 ml-1"><ShieldCheck className="w-4 h-4 fill-blue-500 text-white" /></div>}
                                                                    </div>
                                                                    <div className="text-sm text-slate-400 font-medium">{user.email}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-8 py-5">
                                                            <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold bg-slate-100 text-slate-600 border border-slate-200">
                                                                {user.role}
                                                            </span>
                                                        </td>
                                                        <td className="px-8 py-5">
                                                            {isBlocked ? (
                                                                <span className="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-600 border border-red-200">
                                                                    <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse"></span>
                                                                    <span>Restricted</span>
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-600 border border-emerald-200">
                                                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
                                                                    <span>Active</span>
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-8 py-5 text-center">
                                                            <button
                                                                onClick={() => toggleVerify(user.email)}
                                                                className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${isVerified ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30' : 'bg-slate-100 text-slate-400 hover:bg-slate-200'}`}
                                                            >
                                                                {isVerified ? <ShieldCheck className="w-5 h-5" /> : <div className="w-5 h-5 rounded-full border-2 border-slate-300"></div>}
                                                            </button>
                                                        </td>
                                                        <td className="px-8 py-5 text-right">
                                                            <button
                                                                onClick={() => toggleBlockUser(user.email)}
                                                                className={`inline-flex items-center justify-center w-10 h-10 rounded-xl transition-all shadow-sm ${isBlocked ? 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100 hover:shadow-emerald-200 hover:scale-105' : 'bg-white text-slate-400 border border-slate-200 hover:border-red-200 hover:text-red-600 hover:bg-red-50 hover:shadow-red-200 hover:scale-105'}`}
                                                                title={isBlocked ? "Unblock User" : "Block User"}
                                                            >
                                                                {isBlocked ? <Unlock className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Post Scheme Tab */}
                        {activeTab === 'schemes' && (
                            <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl border border-white/20 p-8 animate-fade-in-up">
                                <div className="mb-8 p-6 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl border border-emerald-100/50">
                                    <h2 className="text-xl font-bold text-slate-800 mb-2">Scheme Publisher</h2>
                                    <p className="text-slate-600 text-sm">Create and broadcast new government initiatives instantly.</p>
                                </div>

                                <form onSubmit={handleSchemeSubmit} className="space-y-8">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <div className="space-y-2">
                                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Scheme Title</label>
                                            <input
                                                required
                                                type="text"
                                                value={newScheme.title}
                                                onChange={e => setNewScheme({ ...newScheme, title: e.target.value })}
                                                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 font-bold text-slate-700 transition-all focus:bg-white"
                                                placeholder="e.g. PM-KISAN"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Category</label>
                                            <div className="relative">
                                                <select
                                                    value={newScheme.category}
                                                    onChange={e => setNewScheme({ ...newScheme, category: e.target.value })}
                                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 font-bold text-slate-700 transition-all focus:bg-white appearance-none cursor-pointer"
                                                >
                                                    <option value="central">Central Government</option>
                                                    <option value="state">State Government</option>
                                                    <option value="other">Other Schemes</option>
                                                </select>
                                                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Description</label>
                                        <textarea
                                            required
                                            rows="4"
                                            value={newScheme.description}
                                            onChange={e => setNewScheme({ ...newScheme, description: e.target.value })}
                                            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 font-medium text-slate-600 transition-all focus:bg-white resize-none"
                                            placeholder="Detailed description of the scheme..."
                                        ></textarea>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Benefits</label>
                                        <input
                                            required
                                            type="text"
                                            value={newScheme.benefits}
                                            onChange={e => setNewScheme({ ...newScheme, benefits: e.target.value })}
                                            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 font-medium text-slate-600 transition-all focus:bg-white"
                                            placeholder="Separate multiple benefits with commas"
                                        />
                                        <p className="text-xs text-slate-400 ml-1">Example: Direct cash transfer, Insurance cover, Low interest loan</p>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <div className="space-y-2">
                                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Eligibility Criteria</label>
                                            <input
                                                required
                                                type="text"
                                                value={newScheme.eligibility}
                                                onChange={e => setNewScheme({ ...newScheme, eligibility: e.target.value })}
                                                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 font-medium text-slate-600 transition-all focus:bg-white"
                                                placeholder="e.g. All land-holding farmer families"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Official Link</label>
                                            <input
                                                required
                                                type="url"
                                                value={newScheme.link}
                                                onChange={e => setNewScheme({ ...newScheme, link: e.target.value })}
                                                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 font-medium text-blue-600 transition-all focus:bg-white"
                                                placeholder="https://..."
                                            />
                                        </div>
                                    </div>

                                    <div className="pt-6 border-t border-slate-100">
                                        <button type="submit" className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold py-4 rounded-xl shadow-lg shadow-emerald-600/30 transition-all hover:scale-[1.02] active:scale-95 flex items-center justify-center space-x-3 group">
                                            <Plus className="w-6 h-6 group-hover:rotate-90 transition-transform" />
                                            <span className="text-lg">Publish Scheme</span>
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
